import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FcGoogle } from 'react-icons/fc';
import { Card, Button, Select, Badge, Input } from '../../components/ui/Kit';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';
import Spinner from '../../components/ui/Spinner';

const US_TIMEZONES = [
  { value: 'America/New_York', label: 'America/New_York (Eastern — default)' },
  { value: 'America/Chicago', label: 'America/Chicago (Central)' },
  { value: 'America/Denver', label: 'America/Denver (Mountain)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (Pacific)' },
  { value: 'America/Anchorage', label: 'America/Anchorage (Alaska)' },
  { value: 'America/Honolulu', label: 'America/Honolulu (Hawaii)' },
];

const DURATION_OPTIONS = [15, 30, 45, 60].map((m) => ({
  value: String(m),
  label: `${m} min`,
}));

const REMINDER_OPTIONS = [
  { value: '30', label: '30 min before' },
  { value: '60', label: '1 hour before' },
  { value: '120', label: '2 hours before' },
  { value: '1440', label: '24 hours before' },
];

const DAY_ROWS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const TEMPLATE_VARS = ['{{firstName}}', '{{agentName}}', '{{appointmentDate}}', '{{appointmentTime}}'];

const DEFAULT_AVAILABILITY = () => ({
  monday: { enabled: true, start: '09:00', end: '18:00' },
  tuesday: { enabled: true, start: '09:00', end: '18:00' },
  wednesday: { enabled: true, start: '09:00', end: '18:00' },
  thursday: { enabled: true, start: '09:00', end: '18:00' },
  friday: { enabled: true, start: '09:00', end: '18:00' },
  saturday: { enabled: false, start: '09:00', end: '13:00' },
  sunday: { enabled: false, start: '09:00', end: '13:00' },
});

function buildTimeOptions() {
  const opts = [];
  for (let h = 6; h <= 23; h++) {
    for (const m of [0, 30]) {
      const val = `${String(h).padStart(2, '0')}:${m === 0 ? '00' : '30'}`;
      const hour12 = h % 12 === 0 ? 12 : h % 12;
      const ampm = h < 12 ? 'AM' : 'PM';
      const label = `${hour12}:${m === 0 ? '00' : '30'} ${ampm}`;
      opts.push({ value: val, label });
      if (h === 23 && m === 30) break;
    }
  }
  return opts;
}

const TIME_OPTIONS = buildTimeOptions();

function normalizeAvailability(raw) {
  const base = DEFAULT_AVAILABILITY();
  if (!raw || typeof raw !== 'object') return base;
  for (const { key } of DAY_ROWS) {
    const d = raw[key];
    if (d && typeof d === 'object') {
      base[key] = {
        enabled: Boolean(d.enabled),
        start: typeof d.start === 'string' ? d.start : base[key].start,
        end: typeof d.end === 'string' ? d.end : base[key].end,
      };
    }
  }
  return base;
}

function ToggleSwitch({ checked, onChange, disabled, 'aria-labelledby': ariaLabelledby }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={ariaLabelledby}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? 'bg-primary' : 'bg-gray-200'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 translate-x-0.5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

function TemplateChips({ onInsert }) {
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {TEMPLATE_VARS.map((v) => (
        <button
          key={v}
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onInsert(v)}
          className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 hover:border-gray-300 transition-colors"
        >
          {v}
        </button>
      ))}
    </div>
  );
}

const ADMIN_AGENCY_CALENDAR_ROLES = ['admin', 'super_admin'];

export default function CalendarTab() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [agentRow, setAgentRow] = useState(null);
  const [agencyRow, setAgencyRow] = useState(null);

  const [timezone, setTimezone] = useState('America/New_York');
  const [defaultDuration, setDefaultDuration] = useState('30');
  const [defaultCustomLink, setDefaultCustomLink] = useState('');
  const [savingCustomLink, setSavingCustomLink] = useState(false);
  const [availability, setAvailability] = useState(DEFAULT_AVAILABILITY);

  const [smsConfirmOn, setSmsConfirmOn] = useState(true);
  const [smsConfirmTpl, setSmsConfirmTpl] = useState('');
  const confirmRef = useRef(null);

  const [smsReminderOn, setSmsReminderOn] = useState(true);
  const [smsReminderMinutes, setSmsReminderMinutes] = useState('60');
  const [smsReminderTpl, setSmsReminderTpl] = useState('');
  const reminderRef = useRef(null);

  const [savingAvail, setSavingAvail] = useState(false);
  const [savingSms, setSavingSms] = useState(false);
  const [connectingAgent, setConnectingAgent] = useState(false);
  const [connectingAgency, setConnectingAgency] = useState(false);

  const isAdminAgencyCalendar = ADMIN_AGENCY_CALENDAR_ROLES.includes(user?.role);

  const loadAgentSettings = useCallback(async () => {
    const row = await api.agentCalendarSettings.get();
    setAgentRow(row);
    setTimezone(row.timezone || 'America/New_York');
    setDefaultDuration(String(row.default_duration_minutes ?? 30));
    setDefaultCustomLink(row.default_custom_link ?? '');
    setAvailability(normalizeAvailability(row.availability));
    setSmsConfirmOn(row.sms_confirmation_enabled !== false);
    setSmsConfirmTpl(row.sms_confirmation_template ?? '');
    setSmsReminderOn(row.sms_reminder_enabled !== false);
    const rm = Number(row.sms_reminder_minutes_before);
    const allowed = [30, 60, 120, 1440];
    setSmsReminderMinutes(String(allowed.includes(rm) ? rm : 60));
    setSmsReminderTpl(row.sms_reminder_template ?? '');
    return row;
  }, []);

  const loadAgencySettings = useCallback(async () => {
    if (!isAdminAgencyCalendar) return null;
    const row = await api.agentCalendarSettings.getAgencySettings();
    setAgencyRow(row);
    return row;
  }, [isAdminAgencyCalendar]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      await loadAgentSettings();
      await loadAgencySettings();
    } catch (e) {
      console.error(e);
      addToast({
        type: 'error',
        title: 'Could not load calendar settings',
        message: e?.message || 'Try again shortly.',
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, loadAgentSettings, loadAgencySettings]);

  useEffect(() => {
    void refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    const connected = searchParams.get('google_connected');
    const err = searchParams.get('google_error');
    if (connected === 'true') {
      addToast({
        type: 'success',
        title: 'Google Calendar connected successfully',
      });
      searchParams.delete('google_connected');
      setSearchParams(searchParams, { replace: true });
      void loadAgentSettings();
      if (isAdminAgencyCalendar) void loadAgencySettings();
    } else if (err === 'true') {
      addToast({
        type: 'error',
        title: 'Failed to connect Google Calendar. Please try again.',
      });
      searchParams.delete('google_error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, addToast, loadAgentSettings, loadAgencySettings, isAdminAgencyCalendar]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const zoom = params.get('zoom');
    if (zoom !== 'connected' && zoom !== 'error') return;

    if (zoom === 'connected') {
      addToast({
        type: 'success',
        title: 'Zoom connected successfully',
      });
      void loadAgentSettings();
    } else {
      addToast({
        type: 'error',
        title: 'Failed to connect Zoom. Please try again.',
      });
    }

    params.delete('zoom');
    const newSearch = params.toString();
    const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${window.location.hash}`;
    window.history.replaceState(null, '', newUrl);
  }, [addToast, loadAgentSettings]);

  const insertConfirmVar = (token) => {
    const el = confirmRef.current;
    setSmsConfirmTpl((prev) => {
      const start = el?.selectionStart ?? prev.length;
      const end = el?.selectionEnd ?? start;
      const next = prev.slice(0, start) + token + prev.slice(end);
      requestAnimationFrame(() => {
        if (!el) return;
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      });
      return next;
    });
  };

  const insertReminderVar = (token) => {
    const el = reminderRef.current;
    setSmsReminderTpl((prev) => {
      const start = el?.selectionStart ?? prev.length;
      const end = el?.selectionEnd ?? start;
      const next = prev.slice(0, start) + token + prev.slice(end);
      requestAnimationFrame(() => {
        if (!el) return;
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      });
      return next;
    });
  };

  const handleConnectAgent = async () => {
    setConnectingAgent(true);
    try {
      const url = await api.googleCalendar.getAuthUrl('agent');
      window.location.href = url;
    } catch (e) {
      console.error(e);
      addToast({
        type: 'error',
        title: 'Could not start Google connection',
        message: e?.message || 'Try again.',
      });
      setConnectingAgent(false);
    }
  };

  const handleDisconnectAgent = async () => {
    try {
      await api.googleCalendar.disconnect('agent');
      addToast({ type: 'success', title: 'Google Calendar disconnected' });
      await loadAgentSettings();
    } catch (e) {
      addToast({
        type: 'error',
        title: 'Disconnect failed',
        message: e?.message || 'Try again.',
      });
    }
  };

  const handleSaveDefaultCustomLink = async () => {
    setSavingCustomLink(true);
    try {
      await api.agentCalendarSettings.update({
        default_custom_link: defaultCustomLink.trim() || null,
      });
      addToast({ type: 'success', title: 'Default meeting link saved' });
      await loadAgentSettings();
    } catch (e) {
      addToast({
        type: 'error',
        title: 'Save failed',
        message: e?.message || 'Try again.',
      });
    } finally {
      setSavingCustomLink(false);
    }
  };

  const handleDisconnectZoom = async () => {
    try {
      // Non-secret flags only — trigger clears agent_zoom_tokens server-side
      await api.agentCalendarSettings.update({
        zoom_connected: false,
        zoom_user_id: null,
      });
      addToast({ type: 'success', title: 'Zoom disconnected' });
      await loadAgentSettings();
    } catch (e) {
      addToast({
        type: 'error',
        title: 'Disconnect failed',
        message: e?.message || 'Try again.',
      });
    }
  };

  const handleConnectAgency = async () => {
    setConnectingAgency(true);
    try {
      const url = await api.googleCalendar.getAuthUrl('agency');
      window.location.href = url;
    } catch (e) {
      console.error(e);
      addToast({
        type: 'error',
        title: 'Could not start agency Google connection',
        message: e?.message || 'Try again.',
      });
      setConnectingAgency(false);
    }
  };

  const handleDisconnectAgency = async () => {
    try {
      await api.googleCalendar.disconnect('agency');
      addToast({ type: 'success', title: 'Agency Google Calendar disconnected' });
      await loadAgencySettings();
    } catch (e) {
      addToast({
        type: 'error',
        title: 'Disconnect failed',
        message: e?.message || 'Try again.',
      });
    }
  };

  const handleSaveAvailability = async () => {
    setSavingAvail(true);
    try {
      await api.agentCalendarSettings.update({
        availability,
        timezone,
        default_duration_minutes: parseInt(defaultDuration, 10),
      });
      addToast({ type: 'success', title: 'Availability saved' });
      await loadAgentSettings();
    } catch (e) {
      addToast({
        type: 'error',
        title: 'Save failed',
        message: e?.message || 'Try again.',
      });
    } finally {
      setSavingAvail(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSavingSms(true);
    try {
      await api.agentCalendarSettings.update({
        sms_confirmation_enabled: smsConfirmOn,
        sms_confirmation_template: smsConfirmTpl,
        sms_reminder_enabled: smsReminderOn,
        sms_reminder_minutes_before: parseInt(smsReminderMinutes, 10),
        sms_reminder_template: smsReminderTpl,
      });
      addToast({ type: 'success', title: 'Notification settings saved' });
      await loadAgentSettings();
    } catch (e) {
      addToast({
        type: 'error',
        title: 'Save failed',
        message: e?.message || 'Try again.',
      });
    } finally {
      setSavingSms(false);
    }
  };

  const patchDay = (key, patch) => {
    setAvailability((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...patch },
    }));
  };

  if (loading && !agentRow) {
    return (
      <div className="flex justify-center py-16 text-gray-500 gap-3">
        <Spinner className="text-primary h-8 w-8" />
        <span className="text-sm">Loading calendar settings…</span>
      </div>
    );
  }

  const googleConnected = Boolean(agentRow?.google_connected);
  const zoomConnected = Boolean(agentRow?.zoom_connected);

  return (
    <div className="space-y-6">
      {/* Section 1 — My Google Calendar */}
      <Card className="p-6 space-y-4">
        <h2 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-3">
          Google Calendar Integration
        </h2>
        {!googleConnected ? (
          <>
            <p className="text-sm text-gray-600">
              Connect your Google Calendar to check your availability and automatically add appointments to your calendar.
            </p>
            <Button
              variant="primary"
              disabled={connectingAgent}
              onClick={() => void handleConnectAgent()}
              className="bg-blue-600 hover:bg-blue-700 shadow-sm border-transparent"
            >
              <FcGoogle className="mr-2 h-5 w-5 shrink-0" aria-hidden />
              Connect Google Calendar
            </Button>
          </>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
            <div>
              <Badge variant="success" className="mb-2">
                ✓ Connected
              </Badge>
              <p className="text-xs text-gray-500 font-mono break-all">
                {agentRow?.google_calendar_id || 'Calendar'}
              </p>
            </div>
            <Button
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50 shrink-0"
              onClick={() => void handleDisconnectAgent()}
            >
              Disconnect
            </Button>
          </div>
        )}
      </Card>

      {/* Section 1b — Zoom */}
      <Card className="p-6 space-y-4">
        <h2 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-3">
          Zoom Integration
        </h2>
        {!zoomConnected ? (
          <p className="text-sm text-gray-600">
            Zoom integration is currently in review with Zoom and isn&apos;t available for new
            connections yet. Set a Default Custom Meeting Link below so your bookings still include
            a working meeting link.
          </p>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
            <div>
              <Badge variant="success" className="mb-2">
                ✓ Zoom Connected
              </Badge>
              <p className="text-xs text-gray-500 font-mono break-all">
                {agentRow?.zoom_user_id || 'Zoom account'}
              </p>
            </div>
            <Button
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50 shrink-0"
              onClick={() => void handleDisconnectZoom()}
            >
              Disconnect
            </Button>
          </div>
        )}

        <div className="pt-2 border-t border-gray-100 space-y-3">
          <Input
            label="Default Custom Meeting Link"
            value={defaultCustomLink}
            onChange={(e) => setDefaultCustomLink(e.target.value)}
            placeholder="https://meet.google.com/… or https://calendly.com/…"
            disabled={savingCustomLink}
          />
          <p className="text-xs text-gray-500">
            Used when a booking uses Custom Link. If empty, public and CRM bookings fall back to{' '}
            <span className="font-mono">mailto:{user?.email || 'your-email'}</span> until you set a
            real link.
          </p>
          <Button
            variant="primary"
            disabled={savingCustomLink}
            isLoading={savingCustomLink}
            onClick={() => void handleSaveDefaultCustomLink()}
          >
            Save meeting link
          </Button>
        </div>
      </Card>

      {/* Section 2 — Availability */}
      <Card className="p-6 space-y-5">
        <h2 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-3">My Availability</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            options={US_TIMEZONES}
          />
          <Select
            label="Default appointment duration"
            value={defaultDuration}
            onChange={(e) => setDefaultDuration(e.target.value)}
            options={DURATION_OPTIONS}
          />
        </div>

        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700">Weekly availability</p>
          <div className="rounded-lg border border-gray-100 divide-y divide-gray-100 overflow-hidden">
            {DAY_ROWS.map(({ key, label }) => {
              const row = availability[key];
              const enabled = row.enabled;
              return (
                <div
                  key={key}
                  className={`flex flex-col lg:flex-row lg:items-center gap-3 p-3 bg-white ${
                    !enabled ? 'opacity-60' : ''
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-[12rem]">
                    <ToggleSwitch checked={enabled} onChange={(v) => patchDay(key, { enabled: v })} />
                    <span className="text-sm font-medium text-gray-900">{label}</span>
                  </div>
                  {enabled ? (
                    <div className="flex flex-wrap items-center gap-2 flex-1">
                      <select
                        value={row.start}
                        onChange={(e) => patchDay(key, { start: e.target.value })}
                        className="flex h-10 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        {TIME_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      <span className="text-sm text-gray-500">to</span>
                      <select
                        value={row.end}
                        onChange={(e) => patchDay(key, { end: e.target.value })}
                        className="flex h-10 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        {TIME_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 lg:ml-auto">Unavailable</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="button" onClick={() => void handleSaveAvailability()} disabled={savingAvail} isLoading={savingAvail}>
            Save Availability
          </Button>
        </div>
      </Card>

      {/* Section 3 — SMS */}
      <Card className="p-6 space-y-6">
        <h2 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-3">
          Appointment SMS Notifications
        </h2>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <span id="sms-confirm-label" className="text-sm font-medium text-gray-800">
              Send SMS confirmation when appointment is booked
            </span>
            <ToggleSwitch checked={smsConfirmOn} onChange={setSmsConfirmOn} aria-labelledby="sms-confirm-label" />
          </div>
          {smsConfirmOn && (
            <div>
              <label htmlFor="sms-confirm-body" className="block text-sm font-medium text-gray-700 mb-1">
                Confirmation template
              </label>
              <textarea
                ref={confirmRef}
                id="sms-confirm-body"
                rows={4}
                value={smsConfirmTpl}
                onChange={(e) => setSmsConfirmTpl(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <TemplateChips onInsert={insertConfirmVar} />
            </div>
          )}
        </div>

        <div className="space-y-3 border-t border-gray-100 pt-6">
          <div className="flex items-center justify-between gap-4">
            <span id="sms-reminder-label" className="text-sm font-medium text-gray-800">
              Send SMS reminder before appointment
            </span>
            <ToggleSwitch checked={smsReminderOn} onChange={setSmsReminderOn} aria-labelledby="sms-reminder-label" />
          </div>
          {smsReminderOn && (
            <div className="space-y-3">
              <Select
                label="Reminder timing"
                value={smsReminderMinutes}
                onChange={(e) => setSmsReminderMinutes(e.target.value)}
                options={REMINDER_OPTIONS}
              />
              <div>
                <label htmlFor="sms-reminder-body" className="block text-sm font-medium text-gray-700 mb-1">
                  Reminder template
                </label>
                <textarea
                  ref={reminderRef}
                  id="sms-reminder-body"
                  rows={4}
                  value={smsReminderTpl}
                  onChange={(e) => setSmsReminderTpl(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <TemplateChips onInsert={insertReminderVar} />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button type="button" onClick={() => void handleSaveNotifications()} disabled={savingSms} isLoading={savingSms}>
            Save Notification Settings
          </Button>
        </div>
      </Card>

      {/* Section 4 — Agency (admin) */}
      {isAdminAgencyCalendar && (
        <Card className="p-6 space-y-4">
          <h2 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-3">
            Agency Google Calendar
          </h2>
          <p className="text-sm text-gray-600">
            Connect an agency-wide Google Calendar. All appointments will be copied here automatically. This calendar is
            not used to check availability.
          </p>
          {!agencyRow?.google_connected ? (
            <Button
              variant="primary"
              disabled={connectingAgency}
              onClick={() => void handleConnectAgency()}
              className="bg-blue-600 hover:bg-blue-700 shadow-sm border-transparent"
            >
              <FcGoogle className="mr-2 h-5 w-5 shrink-0" aria-hidden />
              Connect Google Calendar
            </Button>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
              <div>
                <Badge variant="success" className="mb-2">
                  ✓ Connected
                </Badge>
                <p className="text-xs text-gray-500 font-mono break-all">
                  {agencyRow?.google_calendar_id || 'Calendar'}
                </p>
              </div>
              <Button
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50 shrink-0"
                onClick={() => void handleDisconnectAgency()}
              >
                Disconnect
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
